import React, { useMemo, useState, useCallback } from 'react';
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { useNavigate } from 'react-router';

import {
  useProjectAuthorizers,
  useProjectAuthorizersActions,
} from '@/providers';
import { getProjectAuthorizerFormPath } from '@/util/routePaths';
import ErrorSnackBar from '@/components/shared/ErrorSnackBar';
import ProjectAuthorizersGridToolbar from './ProjectAuthorizersGridToolbar';

const AUTHORIZER_TYPE_LABELS = {
  none: 'None',
  api_key: 'API Key',
  oidc: 'OIDC',
};

function formatAuthorizerType(type) {
  return AUTHORIZER_TYPE_LABELS[type] || type || '';
}

function formatAuthorizerDetail(row) {
  if (row.type === 'oidc') {
    return row.oidc?.issuer || '';
  }
  if (row.type === 'api_key') {
    const keys = Array.isArray(row.keys) ? row.keys : [];
    return keys
      .map((key_row) => key_row?.client_id)
      .filter((client_id) => client_id)
      .join(', ');
  }
  return '';
}

export default function ProjectAuthorizers({ project }) {
  const navigate = useNavigate();
  const { authorizers, loading, error } = useProjectAuthorizers();
  const { removeAuthorizer, clearError } = useProjectAuthorizersActions();
  const [column_visibility_model, setColumnVisibilityModel] = useState({});

  const editProjectAuthorizer = useCallback(
    (authorizer) => () => {
      navigate(getProjectAuthorizerFormPath(project.id, authorizer.id));
    },
    [navigate, project.id],
  );

  const deleteProjectAuthorizer = useCallback(
    (authorizer) => async () => {
      try {
        await removeAuthorizer(authorizer.id);
      } catch {
        // Error is stored on the authorizers context for ErrorSnackBar.
      }
    },
    [removeAuthorizer],
  );

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        type: 'string',
        flex: 1,
      },
      {
        field: 'type',
        headerName: 'Type',
        type: 'string',
        valueFormatter: (value) => formatAuthorizerType(value),
        flex: 0.5,
      },
      {
        field: 'detail',
        headerName: 'Details',
        type: 'string',
        valueGetter: (value, row) => formatAuthorizerDetail(row),
        flex: 1,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        type: 'actions',
        width: 80,
        getActions: (params) => [
          <GridActionsCellItem
            key='edit'
            icon={<EditIcon />}
            label='Edit'
            onClick={editProjectAuthorizer(params.row)}
            showInMenu
          />,
          <GridActionsCellItem
            key='remove'
            icon={<DeleteIcon />}
            label='Remove'
            onClick={deleteProjectAuthorizer(params.row)}
            showInMenu
          />,
        ],
      },
    ],
    [editProjectAuthorizer, deleteProjectAuthorizer],
  );

  const Toolbar = () => <ProjectAuthorizersGridToolbar project={project} />;

  return (
    <Accordion defaultExpanded>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls='project-authorizers-content'
        id='project-authorizers-header'
      >
        <Typography variant='h6' paragraph gutterBottom>
          Project Authorizers
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <div style={{ width: '100%' }}>
          <div style={{ display: 'flex', height: '100%' }}>
            <div style={{ flexGrow: 1 }}>
              <DataGrid
                autoHeight
                loading={loading}
                getRowId={(row) => row.id}
                columns={columns}
                rows={authorizers || []}
                slots={{
                  toolbar: Toolbar,
                }}
                showToolbar
                columnVisibilityModel={column_visibility_model}
                onColumnVisibilityModelChange={(new_model) =>
                  setColumnVisibilityModel(new_model)
                }
              />
            </div>
          </div>
        </div>
        <ErrorSnackBar message={error} onClose={clearError} />
      </AccordionDetails>
    </Accordion>
  );
}
